import React from 'react';

import {
  HomeOutlined,
  ShopOutlined,
  InboxOutlined,
  ShoppingCartOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useOnboarding } from '@contexts/operator/OnboardingContext';
import { Modal, Card, Row, Col, Typography, Space } from 'antd';
import { useTranslation } from 'react-i18next';

const { Title, Paragraph, Text } = Typography;

/**
 * TutorialSelectionModal - Operator 教學選擇彈窗
 *
 * 提供五種教學流程選擇：
 * 1. 倉庫系統基本設定
 * 2. 建立貨主資料
 * 3. 商品入庫流程
 * 4. 訂單出貨流程
 * 5. 進階功能
 */
const TutorialSelectionModal = () => {
  const { t } = useTranslation();
  const {
    showTutorialSelection,
    startTutorial,
    closeTutorialSelection,
  } = useOnboarding();

  // 定義五種教學（莫蘭迪色系）
  const tutorials = [
    {
      key: 'warehouse_setup',
      title: t('operator.onboarding.tutorials.warehouse_setup.title'),
      description: t('operator.onboarding.tutorials.warehouse_setup.description'),
      icon: <HomeOutlined style={{ fontSize: 36, color: '#5b8fa8' }} />,
      color: '#e8f4f8',
      borderColor: '#b8d4e3',
    },
    {
      key: 'merchant_setup',
      title: t('operator.onboarding.tutorials.merchant_setup.title'),
      description: t('operator.onboarding.tutorials.merchant_setup.description'),
      icon: <ShopOutlined style={{ fontSize: 36, color: '#6b9b6e' }} />,
      color: '#e8f5e9',
      borderColor: '#b8d4ba',
    },
    {
      key: 'inbound_process',
      title: t('operator.onboarding.tutorials.inbound_process.title'),
      description: t('operator.onboarding.tutorials.inbound_process.description'),
      icon: <InboxOutlined style={{ fontSize: 36, color: '#a89460' }} />,
      color: '#fef9e7',
      borderColor: '#e6dbb8',
    },
    {
      key: 'outbound_process',
      title: t('operator.onboarding.tutorials.outbound_process.title'),
      description: t('operator.onboarding.tutorials.outbound_process.description'),
      icon: <ShoppingCartOutlined style={{ fontSize: 36, color: '#8b6b94' }} />,
      color: '#f3e8f5',
      borderColor: '#d4b8d9',
    },
    {
      key: 'advanced',
      title: t('operator.onboarding.tutorials.advanced.title'),
      description: t('operator.onboarding.tutorials.advanced.description'),
      icon: <SettingOutlined style={{ fontSize: 36, color: '#a67369' }} />,
      color: '#fbe9e7',
      borderColor: '#e0b8b0',
    },
  ];

  const handleSelectTutorial = (tutorialKey) => {
    startTutorial(tutorialKey);
  };

  const handleSkip = () => {
    closeTutorialSelection();
  };

  return (
    <Modal
      title={<span style={{ fontSize: 22, fontWeight: 600 }}>{t('operator.onboarding.selection.title')}</span>}
      open={showTutorialSelection}
      onCancel={handleSkip}
      footer={null}
      width={800}
      centered
    >
      <Space direction="vertical" size={24} style={{ width: '100%', marginTop: 24 }}>
        <Paragraph>
          {t('operator.onboarding.selection.description')}
        </Paragraph>

        {/* 第一排：3 個卡片 */}
        <Row gutter={[16, 16]}>
          {tutorials.slice(0, 3).map((tutorial) => (
            <Col key={tutorial.key} xs={24} sm={8}>
              <Card
                hoverable
                onClick={() => handleSelectTutorial(tutorial.key)}
                style={{
                  height: '100%',
                  background: tutorial.color,
                  border: `2px solid ${tutorial.borderColor}`,
                  cursor: 'pointer',
                }}
              >
                <Space direction="vertical" size={12} style={{ width: '100%', textAlign: 'center' }}>
                  <div>{tutorial.icon}</div>
                  <Title level={5} style={{ margin: 0 }}>
                    {tutorial.title}
                  </Title>
                  <Paragraph style={{ margin: 0, fontSize: 13, minHeight: 40 }}>
                    {tutorial.description}
                  </Paragraph>
                </Space>
              </Card>
            </Col>
          ))}
        </Row>

        {/* 第二排：2 個卡片置中 */}
        <Row gutter={[16, 16]} justify="center">
          {tutorials.slice(3, 5).map((tutorial) => (
            <Col key={tutorial.key} xs={24} sm={8}>
              <Card
                hoverable
                onClick={() => handleSelectTutorial(tutorial.key)}
                style={{
                  height: '100%',
                  background: tutorial.color,
                  border: `2px solid ${tutorial.borderColor}`,
                  cursor: 'pointer',
                }}
              >
                <Space direction="vertical" size={12} style={{ width: '100%', textAlign: 'center' }}>
                  <div>{tutorial.icon}</div>
                  <Title level={5} style={{ margin: 0 }}>
                    {tutorial.title}
                  </Title>
                  <Paragraph style={{ margin: 0, fontSize: 13, minHeight: 40 }}>
                    {tutorial.description}
                  </Paragraph>
                </Space>
              </Card>
            </Col>
          ))}
        </Row>

        <Card size="small" style={{ background: '#f0f5ff', border: '1px solid #adc6ff' }}>
          <Paragraph style={{ margin: 0 }}>
            💡 {t('operator.onboarding.selection.tip')}
          </Paragraph>
        </Card>
      </Space>
    </Modal>
  );
};

export default TutorialSelectionModal;
